class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    this.isPlaying = false;
    this.abortController = new AbortController();
    this.intersectionObserver = null;
  }

  connectedCallback() {
    const signal = this.abortController.signal;

    if (this.hasAttribute('data-pause-on-start')) {
      document.addEventListener('mediaStartedPlaying', this.pauseMedia.bind(this), { signal });
    }
    window.addEventListener('dialogClosed', this.pauseMedia.bind(this), { signal });

    const playButton = this.querySelector('[data-deferred-media-button]');
    playButton?.addEventListener('click', () => this.showDeferredMedia(), { signal });
    
    const toggleButton = this.querySelector('[data-toggle-media-button]');
    toggleButton?.addEventListener('click', () => this.toggleMedia(), { signal });

    this.setupAutoplayObserver();
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.intersectionObserver?.disconnect();
  }

  updatePlayPauseHint(isPlaying) {
    const toggleButton = this.querySelector('[data-toggle-media-button]');
    if (toggleButton) {
      toggleButton.classList.remove('hidden');
      const playIcon = toggleButton.querySelector('.icon-play');
      const pauseIcon = toggleButton.querySelector('.icon-pause');
      
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new CustomEvent('mediaStartedPlaying', { bubbles: true, detail: this }));

    const template = this.querySelector('template');
    const content = template?.content.cloneNode(true);

    if (!content) return;

    const videos = Array.from(content.querySelectorAll('video'));
    if (videos.length > 1) {
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const mobileVideo = videos.find((video) =>
        video.hasAttribute('data-mobile-video')
      );
      const desktopVideo = videos.find((video) =>
        video.hasAttribute('data-desktop-video')
      );
      const keepVideo = isMobile ? mobileVideo : desktopVideo;

      if (keepVideo) {
        videos.forEach((video) => {
          if (video !== keepVideo) {
            video.remove();
          }
        });
      }
    }

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus) {
      this.lastElementChild?.focus?.();
    }

    const shouldAutoplay = this.hasAttribute('autoplay');
    const video = this.querySelector('video');
    if (video) {
      const wantsAutoplay = shouldAutoplay || video.hasAttribute('autoplay');
      if (wantsAutoplay) {
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');

        const playPromise = video.play();
        if (playPromise?.catch) {
          playPromise.catch(() => {});
        }
      }
    }
  }

  toggleMedia() {
    this.isPlaying ? this.pauseMedia() : this.playMedia();
  }

  playMedia() {
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      const type = iframe.getAttribute('data-video-type');
      iframe.contentWindow?.postMessage(
        type === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) {
        video.play().catch(err => console.log('Play prevented:', err));
      }
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  pauseMedia() {
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      const type = iframe.getAttribute('data-video-type');
      iframe.contentWindow?.postMessage(
        type === 'youtube'
          ? '{"event":"command","func":"pauseVideo","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  setupAutoplayObserver() {    
    // Controlla se il media ha autoplay
    const hasAutoplay = this.hasAttribute('autoplay');

    if (!hasAutoplay) return;

    // Crea observer per caricare quando diventa visibile
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          // Carica quando almeno il 50% è visibile
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            this.loadContent(false); // false = no focus automatico
            this.intersectionObserver?.disconnect(); // Osserva solo una volta
          }
        });
      },
      {
        threshold: 0.5, // 50% visibile
        rootMargin: '50px' // Pre-carica 50px prima di entrare
      }
    );

    this.intersectionObserver.observe(this);
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}